// Ohne Server-Abhängigkeiten, auch im Admin-Client nutzbar.

export const WEATHER_REGIONS = {
  sueden: { name: "Süden", place: "Maspalomas", lat: 27.76, lng: -15.58, sea: { lat: 27.7, lng: -15.62 } },
  norden: { name: "Norden", place: "Las Palmas", lat: 28.1, lng: -15.42, sea: { lat: 28.29, lng: -15.46 } },
  berge: { name: "Berge", place: "Tejeda, rund 1300 m", lat: 27.99, lng: -15.6, sea: null },
} as const;
export type WeatherRegion = keyof typeof WEATHER_REGIONS;
export const WEATHER_REGION_KEYS = Object.keys(WEATHER_REGIONS) as WeatherRegion[];

export const MONTH_NAMES = ["Januar", "Februar", "März", "April", "Mai", "Juni", "Juli", "August", "September", "Oktober", "November", "Dezember"];
export const MONTH_SHORT = ["Jan", "Feb", "Mär", "Apr", "Mai", "Jun", "Jul", "Aug", "Sep", "Okt", "Nov", "Dez"];

/** Monatswerte: Luft in °C (Mittel der Tageshöchst- und -tiefstwerte), Sonne in Stunden pro Tag, Regentage ab 1 mm, Wasser in °C. */
export type ClimateMonth = { tmax: number; tmin: number; sun: number; rainDays: number; precip: number; sea: number | null };
export const CLIMATE_FIELDS = {
  tmax: { label: "Luft max.", unit: "°C", step: 0.1 },
  tmin: { label: "Luft min.", unit: "°C", step: 0.1 },
  sea: { label: "Wasser", unit: "°C", step: 0.1 },
  sun: { label: "Sonne", unit: "h/Tag", step: 0.1 },
  rainDays: { label: "Regentage", unit: "", step: 0.1 },
  precip: { label: "Niederschlag", unit: "mm", step: 1 },
} as const;
export type ClimateField = keyof typeof CLIMATE_FIELDS;
export const CLIMATE_FIELD_KEYS = Object.keys(CLIMATE_FIELDS) as ClimateField[];

export type Climate = {
  computedAt: string;
  years: [number, number];
  /** Erster Monat mit Wasserdaten (YYYY-MM); die Reihe ist deutlich kürzer als die Luftwerte. */
  seaSince: string | null;
  regions: Record<WeatherRegion, ClimateMonth[]>;
};
/** Redaktionelle Korrekturen: Region → Monat (0–11) → Feld → Wert. */
export type ClimateOverrides = Partial<Record<WeatherRegion, Record<string, Partial<Record<ClimateField, number>>>>>;

export type ForecastDay = {
  date: string; code: number; tmax: number; tmin: number; rainProb: number | null; rain: number; wind: number; uv: number | null; sun: number;
};
export type Forecast = {
  fetchedAt: string;
  regions: Record<WeatherRegion, { now: { temp: number; code: number; wind: number } | null; days: ForecastDay[]; sea: number | null; waves: number | null }>;
};

/** WMO-Wettercode → Beschreibung und Symbol. */
export function weatherCode(code: number): { label: string; icon: "sun" | "partly" | "cloud" | "fog" | "drizzle" | "rain" | "snow" | "thunder" } {
  if (code === 0) return { label: "Sonnig", icon: "sun" };
  if (code === 1) return { label: "Überwiegend sonnig", icon: "partly" };
  if (code === 2) return { label: "Teils bewölkt", icon: "partly" };
  if (code === 3) return { label: "Bewölkt", icon: "cloud" };
  if (code === 45 || code === 48) return { label: "Nebel", icon: "fog" };
  if (code >= 51 && code <= 57) return { label: "Nieselregen", icon: "drizzle" };
  if ((code >= 61 && code <= 67) || (code >= 80 && code <= 82)) return { label: code >= 80 ? "Schauer" : "Regen", icon: "rain" };
  if ((code >= 71 && code <= 77) || code === 85 || code === 86) return { label: "Schnee", icon: "snow" };
  if (code >= 95) return { label: "Gewitter", icon: "thunder" };
  return { label: "Wechselhaft", icon: "partly" };
}

export function mergeClimate(c: Climate, o: ClimateOverrides) {
  const regions = {} as Climate["regions"];
  for (const r of WEATHER_REGION_KEYS) regions[r] = c.regions[r].map((m, i) => ({ ...m, ...(o[r]?.[String(i)] ?? {}) }));
  return { ...c, regions };
}
